"use client";

import { Button } from "../ui/button";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Separator } from "../ui/separator";
import { Card, CardContent, CardHeader } from "../ui/card";
import { DynamicFormField, DynamicFormMessageFixedWrapper } from "../forms/dynamic-form";
import { Input } from "../ui/input";
import { useIntlForm } from "@erp/shared-libs/hooks";
import { MaterialLabelPrintRequest, MaterialLabelPrintSchema } from "@erp/frontend-api/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox } from "../ui/checkbox";
import { Form } from "../ui/form";
import { EllipsisText } from "../ui/ellipsis-text";
import { CheckableSubmitButton } from "../forms/checkable-submit-button";
import { useFormState } from "react-hook-form";
import { BaseList } from "../lists/base-list";

export type PrintPreviewListItemObj = {
  materialVariantId: string;
  name: string;
  variants: string;
  sku: string;
  quantity: number;
};

interface PrintConfirmDialogProps extends PropsWithChildren {
  printPreviewList: PrintPreviewListItemObj[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPrint: (items: MaterialLabelPrintRequest) => void;
}

export const PrintConfirmDialog: FC<PrintConfirmDialogProps> = ({
  children,
  open,
  onOpenChange,
  ...props
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      {/* ================================================================
       * Dialog 內容
       * ================================================================ */}
      <DialogContent
        hideClose
        className="h-full max-w-full rounded-none sm:h-auto sm:max-w-lg sm:rounded-xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <PrintConfirmDialogContent {...props} />
      </DialogContent>
    </Dialog>
  );
};

const PrintConfirmDialogContent: FC<Omit<PrintConfirmDialogProps, "open">> = ({
  printPreviewList,
  onPrint,
}) => {
  // ===============================================
  // Hooks
  // ===============================================
  const { t } = useTranslation(["common"]);

  // ================================================
  // Form
  // ================================================
  const form = useIntlForm<MaterialLabelPrintRequest>({
    mode: "onChange",
    resolver: zodResolver(MaterialLabelPrintSchema),
    reValidateMode: "onChange",
    values: {
      items: printPreviewList.map((item) => ({
        materialVariantId: item.materialVariantId,
        sku: item.sku,
        productName: item.name,
        variants: item.variants,
        quantity: item.quantity,
        checked: true, // 預設全選
      })),
    },
  });

  const formState = useFormState(form);

  // ================================================
  // Variables
  // ===============================================
  const isDisabled = useMemo(
    () => !formState.isValid || formState.isValidating || formState.isSubmitting,
    [formState.isSubmitting, formState.isValid, formState.isValidating]
  );

  // ===============================================
  // Functions
  // ===============================================
  /**
   * 切換全選狀態
   */
  const handleToggleAllChecked = useCallback(() => {
    const isAllChecked = form.getValues("items").every((item) => item.checked);

    if (isAllChecked) {
      form.setValue(
        "items",
        form.getValues("items").map((item) => ({
          ...item,
          checked: false,
        })),
        { shouldDirty: true, shouldValidate: true }
      );
    } else {
      form.setValue(
        "items",
        form.getValues("items").map((item) => ({
          ...item,
          checked: true,
        })),
        { shouldDirty: true, shouldValidate: true }
      );
    }
  }, [form]);

  /**
   * 列印
   */
  const handlePrint = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      form.handleSubmit(async (data) => {
        onPrint({
          ...data,
        });
      })(event);
    },
    [form, onPrint]
  );

  // ===============================================
  // Render
  // ===============================================
  return (
    <Form {...form}>
      <form onSubmit={handlePrint} className="flex flex-col">
        <div className="flex flex-1 flex-col pt-4">
          <DialogHeader className="mb-2 px-4">
            <DialogTitle asChild>
              <div className="text-lg font-bold">{t("common:dialog.print_confirm.title")}</div>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground mb-4 text-sm">
              {t("common:dialog.print_confirm.message")}
            </DialogDescription>
            {/* ================================================================
             * 注意事項
             * ================================================================ */}
            <Card className="bg-grid-paper shadow-none">
              <CardContent className="space-y-2 p-4 text-sm">
                <p className="text-primary text-left font-bold">注意事項</p>
                <ul className="list-disc space-y-1 pl-4 text-left">
                  <li>請確認數量後再執行列印</li>
                  <li>列印時請於系統列印視窗選擇標籤機</li>
                </ul>
              </CardContent>
            </Card>
          </DialogHeader>
          <Separator />
          <div className="bg-background flex flex-1 flex-col gap-4 p-4">
            {/* ================================================================
             * 列印清單
             * ================================================================ */}
            <Card className="bg-on-background shadow-none">
              <CardHeader className="relative py-2.5 text-center text-sm">
                {/* 全選按鈕 */}
                <div className="absolute left-4 top-1/2 flex -translate-y-1/2">
                  <Button
                    type="button"
                    variant="outlinePrimary"
                    size="sm"
                    className="h-6 px-2"
                    onClick={handleToggleAllChecked}
                  >
                    {form.watch("items").every((x) => x.checked) ? "取消全選" : "全選"}
                  </Button>
                </div>
                {/* 列印清單標題 */}
                <p>列印清單</p>
              </CardHeader>
              <Separator />
              <CardContent className="p-0 text-sm">
                <BaseList
                  data={printPreviewList}
                  keyField="materialVariantId"
                  estimateSize={52}
                  maxHeight={240}
                  itemClassName="px-4 py-2"
                  renderItem={(item, index) => (
                    <div className="animate-in fade-in-0 slide-in-from-bottom-2 flex items-center overflow-hidden duration-500">
                      <div>
                        <DynamicFormField
                          control={form.control}
                          name={`items.${index}.checked`}
                          render={({ field }) => (
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked: boolean) => {
                                field.onChange(checked);
                                field.onBlur(checked);
                                // 消除錯誤訊息
                                form.trigger("items");
                              }}
                            />
                          )}
                        />
                      </div>
                      <div className="ml-4 flex flex-1 items-center overflow-hidden">
                        <div className="flex-1 overflow-hidden">
                          <EllipsisText text={item.sku} />
                          <EllipsisText text={`${item.name} - ${item.variants}`} />
                        </div>
                        <div className="ml-auto">
                          <DynamicFormField
                            control={form.control}
                            name={`items.${index}.quantity`}
                            render={({ field }) => (
                              <DynamicFormMessageFixedWrapper>
                                <Input
                                  integer
                                  number
                                  className="m-1 h-9 w-20"
                                  placeholder="請輸入數量"
                                  {...field}
                                  value={field.value || ""}
                                />
                              </DynamicFormMessageFixedWrapper>
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        <Separator />
        <div className="flex items-end justify-end px-4 py-2">
          {/* ================================================================
           * 取消按鈕
           * ================================================================ */}
          <DialogClose asChild>
            <Button variant="outline" size="sm" className="px-8">
              {t("common:button.close")}
            </Button>
          </DialogClose>
          {/* ================================================================
           * 列印按鈕
           * ================================================================ */}
          {/* 列印清單由 values 預填，直接確認列印是正常流程，不要求先有變更 */}
          <CheckableSubmitButton targetForm={form} requireDirty={false} disabled={isDisabled}>
            {t("common:dialog.print_confirm.button.print")}
          </CheckableSubmitButton>
        </div>
      </form>
    </Form>
  );
};
